import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ShoppingBag,
  CheckCircle,
  AlertCircle,
  X,
  Loader2,
  Settings,
  Layout
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import Base from '../Base';

const ShopPageForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fullJsonData, setFullJsonData] = useState(null);

  // Editable content extracted from the JSON blocks
  const [formData, setFormData] = useState({
    // Page Header content (from blocks)
    pageTitle: 'Shop',
    pageSubtitle: 'Discover our curated collection of minimalist essentials',
    // Configuration options (stored in content field)
    config: {
      productsPerPage: 12,
      gridColumns: 4,
      showFilters: true,
      showSort: true,
      showSearch: true,
      defaultSort: 'featured',
      enableQuickView: true,
      showBreadcrumbs: true
    }
  });

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  // Helper function to find content in nested structure
  const findContentInChildren = (children, contentTypes) => {
    if (!children) return {};
    const result = {};

    const searchChildren = (items) => {
      for (const item of items) {
        if (item.type === 'h1' && contentTypes.includes('h1')) {
          result.h1 = item.content || '';
        }
        if (item.type === 'p' && contentTypes.includes('p') && !result.p) {
          result.p = item.content || '';
        }
        if (item.children) {
          searchChildren(item.children);
        }
      }
    };

    searchChildren(children);
    return result;
  };

  // Helper function to update content in nested structure
  const updateContentInChildren = (children, updates) => {
    if (!children) return children;

    return children.map(item => {
      const newItem = { ...item };
      if (item.type === 'h1' && updates.h1 !== undefined) {
        newItem.content = updates.h1;
      }
      if (item.type === 'p' && updates.p !== undefined) {
        newItem.content = updates.p;
        updates.p = undefined; // Only update first p tag
      }
      if (item.children) {
        newItem.children = updateContentInChildren(item.children, { ...updates });
      }
      return newItem;
    });
  };

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/template/${templateId}/page/shop`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });

      console.log('Shop page API response:', response.data);

      // Store the full JSON data
      setFullJsonData(response.data);

      // Extract editable content from blocks
      if (response.data.blocks && response.data.blocks.length > 0) {
        const pageHeader = response.data.blocks.find(b => b.id === 'page-header');
        if (pageHeader && pageHeader.structure) {
          const content = findContentInChildren(pageHeader.structure.children, ['h1', 'p']);
          setFormData(prev => ({
            ...prev,
            pageTitle: content.h1 || prev.pageTitle,
            pageSubtitle: content.p || prev.pageSubtitle
          }));
        }
      }

      // Extract config from content field if exists
      if (response.data.content && Object.keys(response.data.content).length > 0) {
        setFormData(prev => ({
          ...prev,
          config: { ...prev.config, ...response.data.content.config }
        }));
      }

    } catch (error) {
      console.error('Error loading shop page data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  const handleSave = async (isDraft = true) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');

      // Prepare updated JSON with edited content
      let updatedJson = fullJsonData ? { ...fullJsonData } : {};

      // Update page header content in blocks
      if (updatedJson.blocks) {
        updatedJson.blocks = updatedJson.blocks.map(block => {
          if (block.id === 'page-header' && block.structure) {
            return {
              ...block,
              structure: {
                ...block.structure,
                children: updateContentInChildren(block.structure.children, {
                  h1: formData.pageTitle,
                  p: formData.pageSubtitle
                })
              }
            };
          }
          return block;
        });
      }

      // Update content field with config
      updatedJson.content = {
        ...updatedJson.content,
        config: formData.config,
        pageTitle: formData.pageTitle,
        pageSubtitle: formData.pageSubtitle
      };

      await axios.post(`${API_BASE_URL}/api/template/${templateId}/page/shop/save`, {
        status: isDraft ? 'draft' : 'complete',
        content: updatedJson.content,
        blocks: updatedJson.blocks
      }, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });

      setLastSaved(new Date());
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving:', error);
      setErrorMessage(error.response?.data?.message || 'Error saving changes. Please try again.');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  // Success Modal Component
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your shop page has been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#D45614] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-gray-500">Loading shop page...</p>
          </div>
        </div>
      } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/template-builder/${templateId}`)}
                className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Shop Page</h1>
                {lastSaved && (
                  <p className="text-xs text-gray-400">Saved {lastSaved.toLocaleTimeString()}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-1.5 bg-[#D45614] text-white text-sm font-medium rounded-lg hover:bg-[#c04d12] disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Page Header Content */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Page Header</h2>
              <p className="text-sm text-gray-500">Title and subtitle shown at the top of the shop page</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Page Title</label>
              <input
                type="text"
                value={formData.pageTitle}
                onChange={(e) => setFormData(prev => ({ ...prev, pageTitle: e.target.value }))}
                placeholder="Shop"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Page Subtitle</label>
              <input
                type="text"
                value={formData.pageSubtitle}
                onChange={(e) => setFormData(prev => ({ ...prev, pageSubtitle: e.target.value }))}
                placeholder="Discover our curated collection of minimalist essentials"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>
          </div>
        </section>

        {/* Display Configuration */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <Layout className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Display Settings</h2>
              <p className="text-sm text-gray-500">Configure how products are displayed</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Products Per Page</label>
                <select
                  value={formData.config.productsPerPage}
                  onChange={(e) => setFormData(prev => ({ ...prev, config: { ...prev.config, productsPerPage: parseInt(e.target.value) } }))}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                >
                  <option value={8}>8 Products</option>
                  <option value={12}>12 Products</option>
                  <option value={16}>16 Products</option>
                  <option value={24}>24 Products</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Grid Columns</label>
                <select
                  value={formData.config.gridColumns}
                  onChange={(e) => setFormData(prev => ({ ...prev, config: { ...prev.config, gridColumns: parseInt(e.target.value) } }))}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                >
                  <option value={2}>2 Columns</option>
                  <option value={3}>3 Columns</option>
                  <option value={4}>4 Columns</option>
                  <option value={5}>5 Columns</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Default Sort Order</label>
              <select
                value={formData.config.defaultSort}
                onChange={(e) => setFormData(prev => ({ ...prev, config: { ...prev.config, defaultSort: e.target.value } }))}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              >
                <option value="featured">Featured</option>
                <option value="newest">Newest First</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="name">Name: A to Z</option>
              </select>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <Settings className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Features</h2>
              <p className="text-sm text-gray-500">Toggle shop page features</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { key: 'showFilters', label: 'Show Product Filters', desc: 'Category, price range, etc.' },
              { key: 'showSort', label: 'Show Sort Dropdown', desc: 'Allow sorting products' },
              { key: 'showSearch', label: 'Show Search Bar', desc: 'Search within products' },
              { key: 'enableQuickView', label: 'Enable Quick View', desc: 'Product preview modal' },
              { key: 'showBreadcrumbs', label: 'Show Breadcrumbs', desc: 'Navigation path' }
            ].map(({ key, label, desc }) => (
              <label key={key} className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all border-2 ${
                formData.config[key] ? 'border-[#D45614] bg-[#D45614]/5' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="checkbox"
                  checked={formData.config[key]}
                  onChange={(e) => setFormData(prev => ({ ...prev, config: { ...prev.config, [key]: e.target.checked } }))}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                  formData.config[key] ? 'border-[#D45614] bg-[#D45614]' : 'border-gray-300'
                }`}>
                  {formData.config[key] && <Check className="w-3 h-3 text-white" />}
                </div>
                <div>
                  <span className={`text-sm font-medium block ${formData.config[key] ? 'text-[#D45614]' : 'text-gray-700'}`}>{label}</span>
                  <span className="text-xs text-gray-500">{desc}</span>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* Info Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Products are managed separately in your product catalog. This configuration controls how they are displayed on the shop page. The theme colors are inherited from your main page settings.
          </p>
        </div>
      </main>

      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default ShopPageForm;
